import { type AssetFileKind, type AssetKind, type FileKind } from '@air/api/types';
import {
  BoardsIcon,
  BoxIcon,
  ChartPieIcon,
  FileIcon,
  FileSignalIcon,
  FileTextIcon,
  FolderArchiveIcon,
  FolderIcon,
  type IconComponent,
  ImageIcon,
  ImagesIcon,
  PenToolIcon,
  PlayIcon,
  SquarePlayIcon,
  Table2Icon,
} from '@air/design-icons';

import { type SelectedFilter } from '~/store/filters/types';

export const AllItemTypeFilters = ['boards', 'assets', 'files'] as const;
export type ItemTypeFilter = (typeof AllItemTypeFilters)[number];

export const isItemTypeFilter = (value: string): value is ItemTypeFilter =>
  !!AllItemTypeFilters.find((filter) => filter === value);

export const isAssetTypeFilter = (value: string): value is AssetFileKind =>
  !!Object.values({ ...ASSET_TYPE_FILTERS, ...FILES_TYPE_FILTERS }).find((filter) => filter.id === value);

export type SingleFilterProps = {
  onApplyClick: () => void;
  onRemoveClick?: (filters: SelectedFilter[]) => void;
  onOpenChange?: (open: boolean) => void;
};

export type AssetTypeFilter = {
  id: AssetKind;
  label: string;
  icon: IconComponent;
};

export const ASSET_TYPE_FILTERS: Partial<Record<AssetKind, AssetTypeFilter>> = {
  image: {
    id: 'image',
    label: 'Images',
    icon: ImageIcon,
  },
  video: {
    id: 'video',
    label: 'Videos',
    icon: SquarePlayIcon,
  },
  audio: {
    id: 'audio',
    label: 'Audio & Sounds',
    icon: FileSignalIcon,
  },
  vector: {
    id: 'vector',
    label: 'Illustrator & Vector Graphics',
    icon: PenToolIcon,
  },
  presentation: {
    id: 'presentation',
    label: 'Presentations',
    icon: ChartPieIcon,
  },
  pdf: {
    id: 'pdf',
    label: 'PDFs',
    icon: FileTextIcon,
  },
  document: {
    id: 'document',
    label: 'Documents',
    icon: FileIcon,
  },
  rive: {
    id: 'rive',
    label: 'Animations',
    icon: PlayIcon,
  },
};

export type FilesTypeFilter = {
  id: FileKind;
  label: string;
  icon: IconComponent;
};

export const FILES_TYPE_FILTERS: Record<FileKind, FilesTypeFilter> = {
  compressed: {
    id: 'compressed',
    label: 'Compressed Files',
    icon: FolderArchiveIcon,
  },
  font: {
    id: 'font',
    label: 'Fonts',
    icon: FileIcon,
  },
  spreadsheet: {
    id: 'spreadsheet',
    label: 'Spreadsheets & Databases',
    icon: Table2Icon,
  },
  threeD: {
    id: 'threeD',
    label: '3D Objects',
    icon: BoxIcon,
  },
  other: {
    id: 'other',
    label: 'All other files',
    icon: FileIcon,
  },
};

export type TypeFilters = 'boards' | 'assets' | 'files';

export type TypeFilter = {
  id: TypeFilters;
  label: string;
  icon: IconComponent;
  subFilters?: Record<FileKind, FilesTypeFilter> | Partial<Record<AssetKind, AssetTypeFilter>>;
};

export const TYPE_FILTERS: Record<TypeFilters, TypeFilter> = {
  boards: {
    id: 'boards',
    label: 'Boards',
    icon: BoardsIcon,
  },
  assets: {
    id: 'assets',
    label: 'Assets',
    icon: ImagesIcon,
    subFilters: ASSET_TYPE_FILTERS,
  },
  files: {
    id: 'files',
    label: 'Files',
    icon: FolderIcon,
    subFilters: FILES_TYPE_FILTERS,
  },
};
